import { Link } from "react-router-dom";
import { BarChart2, Star, ChevronRight } from "lucide-react";
import { StarRating } from "@/components/primitives";
import { ZODIAC_SIGNS } from "@/lib/readoo";

const VIOLET = "#8b5cf6";

function signMeta(key) {
  return ZODIAC_SIGNS.find((s) => s.key === key) || {};
}

function signPath(s) {
  return `/articolo/${s.article_id}/oroscopo-${s.key}`;
}

/**
 * HoroscopeHub
 * mode="summary" → full hub (ranking + grid) used on /oroscopo
 * mode="article" → compact grid of other signs + link to riepilogo, used inside an Oroscopo article
 */
export function HoroscopeHub({ data, mode = "summary", currentSign = null }) {
  if (!data || !data.signs || data.signs.length === 0) return null;
  const ranking = data.ranking || [...data.signs].sort((a, b) => b.stars - a.stars);

  if (mode === "article") {
    const others = data.signs.filter((s) => s.key !== currentSign);
    return (
      <div data-testid="horoscope-hub-article" className="my-8 rounded-2xl border p-5 sm:p-6" style={{ borderColor: "rgba(139,92,246,0.35)", backgroundColor: "rgba(139,92,246,0.06)" }}>
        <div className="flex items-center justify-between mb-4">
          <span className="flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-wider" style={{ color: VIOLET }}>
            <Star size={15} /> Gli altri segni, oggi
          </span>
          {data.summary && (
            <Link to={`/articolo/${data.summary.article_id}/oroscopo-riepilogo`} className="text-xs font-semibold hover:underline" style={{ color: VIOLET }}>
              Classifica completa
            </Link>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {others.map((s) => {
            const m = signMeta(s.key);
            return (
              <Link key={s.key} to={signPath(s)} data-testid={`hub-sign-${s.key}`} className="flex items-center gap-2 rounded-xl border border-hair bg-surface px-3 py-2 hover:border-brand/40 transition-colors">
                <span className="text-lg">{m.emoji}</span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-onSurface truncate">{m.name}</p>
                  <p className="text-[11px]" style={{ color: VIOLET }}>{"★".repeat(s.stars)}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="horoscope-hub">
      {/* Ranking */}
      <section className="rounded-2xl border p-5 sm:p-6" style={{ borderColor: "rgba(139,92,246,0.3)", backgroundColor: "rgba(139,92,246,0.06)" }}>
        <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-onSurface mb-4">
          <BarChart2 size={20} style={{ color: VIOLET }} /> Classifica del giorno
        </h2>
        <div className="space-y-1.5">
          {ranking.map((s, i) => {
            const m = signMeta(s.key);
            return (
              <Link
                key={s.key}
                to={signPath(s)}
                data-testid={`hub-rank-${s.key}`}
                className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-surfaceTertiary/60 transition-colors"
              >
                <span className="w-7 h-7 shrink-0 rounded-full flex items-center justify-center font-mono text-xs font-bold" style={{ backgroundColor: i < 3 ? VIOLET : "rgba(139,92,246,0.15)", color: i < 3 ? "#fff" : VIOLET }}>
                  {i + 1}
                </span>
                <span className="text-xl">{m.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-onSurface">{m.name}</p>
                  {s.flash && <p className="text-xs text-subtle truncate">{s.flash}</p>}
                </div>
                <StarRating value={s.stars} className="text-sm" />
                <ChevronRight size={16} className="text-subtle shrink-0" />
              </Link>
            );
          })}
        </div>
      </section>

      {/* Grid */}
      <section>
        <h2 className="font-serif text-xl font-semibold text-onSurface mb-4">Il tuo segno, oggi</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {data.signs.map((s) => {
            const m = signMeta(s.key);
            return (
              <Link
                key={s.key}
                to={signPath(s)}
                data-testid={`hub-grid-${s.key}`}
                className="group rounded-2xl border border-hair bg-surfaceSecondary p-4 hover:border-brand/40 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="text-2xl">{m.emoji}</span>
                  <span className="text-[10px] font-mono text-subtle">{m.dates}</span>
                </div>
                <p className="mt-2 font-serif text-base font-semibold text-onSurface group-hover:text-brand transition-colors">{m.name}</p>
                <p className="mt-1" style={{ color: VIOLET }}>{"★".repeat(s.stars)}<span className="text-hairStrong">{"★".repeat(5 - s.stars)}</span></p>
                {s.flash && <p className="mt-1 text-xs text-subtle line-clamp-2">{s.flash}</p>}
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
